import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { Reveal } from "../Reveal";
import { MagneticButton } from "../MagneticButton";
import { PRICING, ADDONS } from "../../lib/data";

export default function Pricing() {
  const go = () => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="pricing" data-testid="pricing-section" className="relative bg-ink-surface py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="text-center">
          <Reveal><span className="text-xs font-bold uppercase tracking-[0.3em] text-gold">Transparent Pricing</span></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Simple, honest rates.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-md text-white/55">
              Oil-change pricing below. Final pricing depends on your vehicle — we'll always confirm at booking.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PRICING.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <motion.div
                whileHover={{ y: -8 }}
                data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative flex h-full flex-col rounded-3xl border p-7 ${
                  p.featured
                    ? "border-gold bg-gradient-to-b from-[#1a1800] to-ink-surface gold-glow"
                    : "border-ink-soft bg-ink"
                }`}
              >
                {p.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gold px-4 py-1 text-xs font-bold text-ink">
                    <Star className="mr-1 inline h-3 w-3 fill-ink" strokeWidth={0} /> MOST POPULAR
                  </div>
                )}
                <h3 className="font-display text-2xl font-medium">{p.name}</h3>
                <p className="mt-1 text-sm text-white/50">{p.tagline}</p>
                <div className="mt-6 flex items-end gap-1">
                  {p.from && <span className="mb-1 text-sm text-white/50">from</span>}
                  <span className="font-display text-4xl font-semibold text-gold">${p.price}</span>
                </div>
                <ul className="mt-7 flex-1 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-white/75">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold"><Check className="h-3 w-3" /></span>
                      {f}
                    </li>
                  ))}
                </ul>
                <MagneticButton
                  onClick={go}
                  data-testid={`pricing-book-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`mt-8 w-full rounded-full py-3 font-semibold transition-colors ${
                    p.featured ? "bg-gold text-ink hover:bg-gold-hover" : "border border-white/15 text-white hover:border-gold hover:text-gold"
                  }`}
                >
                  Book {p.name}
                </MagneticButton>
              </motion.div>
            </Reveal>
          ))}
        </div>

        {/* Additional services */}
        <Reveal delay={0.1}>
          <div className="mt-14 rounded-3xl border border-ink-soft bg-ink p-8 md:p-10" data-testid="addons-block">
            <div className="mb-8 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
              <h3 className="font-display text-2xl font-medium">Also available, fully mobile</h3>
              <button onClick={go} className="text-sm font-medium text-gold hover:underline" data-testid="addons-book">Book any service →</button>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {ADDONS.map((a, i) => (
                <div key={a.title} data-testid={`addon-${i}`} className="group rounded-2xl border border-ink-soft bg-ink-surface p-6 transition-colors hover:border-gold/40">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/15 text-gold transition-colors group-hover:bg-gold group-hover:text-ink">
                    <a.icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <h4 className="mt-5 font-display text-lg font-medium">{a.title}</h4>
                  <p className="mt-2 text-sm text-white/55">{a.desc}</p>
                  <div className="mt-4 font-display text-xl font-semibold text-gold">{a.price}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
